/**
 * BcpIngestionCard — ingestion health for the BCP (Budget Change Proposal)
 * pipeline, on the Scraper Ops page next to the CaleProcure probe.
 *
 * The gap this fills: the BCP corpus can LOOK current — recent May Revision
 * documents sitting in the table — while the harvester on the Mini has been
 * dead for weeks. Reading freshness off the records is a guess. This card
 * shows the evidence instead, in the order the question actually gets asked:
 *
 *   last scrape · last new BCP · DOF's count vs ours · failed downloads ·
 *   missing / changed documents
 *
 * Data source: GET /api/v1/bcps/health (services/bcpHealthCore). The source
 * comparison is the only proof of completeness, so when it did not run the
 * card says so in place of the number rather than showing counts that look
 * reconciled. Reuses SchedulerHealthCard's .schcard CSS, like ProbeStatusCard.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronRight, Clock,
  FileWarning, HelpCircle, Loader2, RefreshCw,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SchedulerHealthCard.css';

function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const num = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));

/** Card state ← report status. 'unknown' keeps the neutral (disabled) tone. */
const STATE = {
  healthy: { cls: 'running', label: 'HEALTHY', Icon: CheckCircle },
  degraded: { cls: 'waiting', label: 'DEGRADED', Icon: Clock },
  unhealthy: { cls: 'stalled', label: 'UNHEALTHY', Icon: AlertTriangle },
  unknown: { cls: 'disabled', label: 'UNKNOWN', Icon: HelpCircle },
};

const LEVEL_CLASS = { critical: 'is-bad', warning: 'is-warn', info: 'is-muted' };

const QUERY_KEY = ['bcp', 'ingestion-health'];

export default function BcpIngestionCard() {
  const [issuesOpen, setIssuesOpen] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);

  const queryClient = useQueryClient();
  const [rechecking, setRechecking] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get('/api/v1/bcps/health', { params: { probe: 'current' } }),
    // The endpoint caches its DOF probe for 10 minutes; polling faster than
    // that would only re-serve the same source numbers.
    refetchInterval: 10 * 60 * 1000,
  });

  // A plain refetch would re-serve the server's cached probe, so "Re-check"
  // asks for a genuinely fresh comparison instead — the whole point of the
  // button is to go look at DOF again.
  const recheck = async () => {
    setRechecking(true);
    try {
      const fresh = await apiClient.get('/api/v1/bcps/health', {
        params: { probe: 'current', refresh: 1 },
      });
      queryClient.setQueryData(QUERY_KEY, fresh);
    } finally {
      setRechecking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="schcard schcard--loading">
        <Loader2 size={16} className="schcard-spin" />
        <span>Loading BCP ingestion health…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schcard schcard--error">
        <AlertTriangle size={16} />
        <span>BCP ingestion health unreachable: {error.message}</span>
      </div>
    );
  }

  const state = STATE[data?.status] || STATE.unknown;
  const { Icon } = state;
  const corpus = data?.corpus || {};
  const pipeline = data?.pipeline || {};
  const source = data?.source || {};
  const issues = data?.issues || [];
  const documents = data?.documents || {};
  const sourceLive = source.mode === 'live' && source.totals;

  const missingDocs = documents.missingDownloads || [];
  const failedParses = documents.failedParses || [];
  const changed = documents.changed || [];
  const attention = missingDocs.length + failedParses.length + changed.length;

  return (
    <div className={`schcard schcard--${state.cls}`}>
      <div className="schcard-header">
        <div className="schcard-header-left">
          <Icon size={16} className="schcard-icon" />
          <span className="schcard-title">BCP Ingestion</span>
          <span className={`schcard-badge schcard-badge--${state.cls}`}>{state.label}</span>
        </div>
        <button
          type="button"
          className="schcard-section-toggle"
          onClick={recheck}
          disabled={rechecking}
          title="Re-check against DOF now (bypasses the 10-minute probe cache)"
        >
          <RefreshCw size={13} className={rechecking ? 'schcard-spin' : undefined} />
          <span>Re-check</span>
        </button>
      </div>

      {data?.headline && (
        <div className="schcard-nudges">
          <div className="schcard-nudge">{data.headline}</div>
        </div>
      )}

      {/* The five numbers the question is actually about. */}
      <div className="schcard-grid">
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last scrape</span>
          <span className="schcard-metric-value">
            {timeAgo(data?.scrape?.lastSuccessfulHarvestAt)}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last new BCP</span>
          <span className="schcard-metric-value">
            {timeAgo(corpus.lastNewBcpAt)}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">
            {sourceLive ? `DOF vs held (${source.scope})` : 'DOF vs held'}
          </span>
          <span
            className={`schcard-metric-value ${
              !sourceLive ? 'is-muted' : source.totals.missing > 0 ? 'is-bad' : 'is-good'
            }`}
          >
            {sourceLive
              ? `${num(source.totals.source)} / ${num(source.totals.database)}`
              : 'not checked'}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Failed downloads</span>
          <span className={`schcard-metric-value ${pipeline.missingDownload > 0 ? 'is-bad' : 'is-good'}`}>
            {num(pipeline.missingDownload)}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Parse failures</span>
          <span className={`schcard-metric-value ${pipeline.parseFailed > 0 ? 'is-warn' : 'is-good'}`}>
            {num(pipeline.parseFailed)}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Documents held</span>
          <span className="schcard-metric-value">{num(corpus.documents)}</span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">New in 30d</span>
          <span className="schcard-metric-value">{num(corpus.newLast30Days)}</span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Changed at DOF</span>
          <span className={`schcard-metric-value ${pipeline.changedNotReparsed > 0 ? 'is-warn' : ''}`}>
            {num(pipeline.changedDocuments)}
            {pipeline.changedNotReparsed > 0 ? ` (${pipeline.changedNotReparsed} unparsed)` : ''}
          </span>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="schcard-section">
          <button
            type="button"
            className="schcard-section-toggle"
            onClick={() => setIssuesOpen((v) => !v)}
          >
            {issuesOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span>{issues.length} issue{issues.length === 1 ? '' : 's'}</span>
          </button>
          {issuesOpen && (
            <ul className="schcard-sublist">
              {issues.map((issue) => (
                <li
                  key={issue.code + issue.message}
                  className={`schcard-sublist-item ${LEVEL_CLASS[issue.level] || 'is-muted'}`}
                >
                  <span>{issue.message}</span>
                  <span className="schcard-sublist-right">{issue.level}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {attention > 0 && (
        <div className="schcard-section">
          <button
            type="button"
            className="schcard-section-toggle"
            onClick={() => setDetailOpen((v) => !v)}
          >
            {detailOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <FileWarning size={13} />
            <span>Documents needing attention ({attention})</span>
          </button>
          {detailOpen && (
            <ul className="schcard-sublist">
              {missingDocs.map((d) => (
                <li key={`miss-${d.bcpId}`} className="schcard-sublist-item is-bad">
                  <span>Never downloaded · BCP {d.bcpId} · {d.title}</span>
                  <span className="schcard-sublist-right">{d.fiscalYear} {d.periodLabel}</span>
                </li>
              ))}
              {failedParses.map((d) => (
                <li key={`fail-${d.bcpId}`} className="schcard-sublist-item is-warn">
                  <span>Parse failed · BCP {d.bcpId} · {d.parseError || 'no error recorded'}</span>
                  <span className="schcard-sublist-right">{d.fiscalYear} {d.periodLabel}</span>
                </li>
              ))}
              {changed.map((d) => (
                <li
                  key={`chg-${d.bcpId}`}
                  className={`schcard-sublist-item ${d.parseStatus === 'parsed' ? 'is-muted' : 'is-warn'}`}
                >
                  <span>
                    Replaced at DOF ({d.versions} versions) · BCP {d.bcpId} · {d.title}
                  </span>
                  <span className="schcard-sublist-right">
                    {d.parseStatus}{d.analyzed ? '' : ' · not analyzed'}
                  </span>
                </li>
              ))}
              {attention >= (documents.detailLimit || 25) && (
                <li className="schcard-sublist-item is-muted">
                  <span>Showing the most recent {documents.detailLimit} — more may exist.</span>
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {(source.byPair || []).length > 0 && (
        <div className="schcard-section">
          <button
            type="button"
            className="schcard-section-toggle"
            onClick={() => setCoverageOpen((v) => !v)}
          >
            {coverageOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span>Source comparison by budget cycle</span>
          </button>
          {coverageOpen && (
            <ul className="schcard-sublist">
              {source.byPair.map((p) => (
                <li
                  key={`${p.fiscalYear}-${p.period}`}
                  className={`schcard-sublist-item ${
                    p.status !== 'ok' ? 'is-muted' : p.missing > 0 ? 'is-bad' : 'is-good'
                  }`}
                >
                  <span>FY {p.fiscalYear} · {p.periodLabel}</span>
                  <span className="schcard-sublist-right">
                    {p.status === 'ok'
                      ? `${num(p.sourceCount)} at DOF / ${num(p.databaseCount)} held${
                          p.missing > 0 ? ` · ${p.missing} missing` : ''
                        }${p.extra > 0 ? ` · ${p.extra} delisted` : ''}`
                      : p.status === 'absent'
                        ? 'not published by DOF'
                        : `unreadable: ${p.error}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="schcard-footer">
        {sourceLive
          ? `Source checked ${timeAgo(source.checkedAt)} against ${source.baseUrl}`
          : `Completeness NOT verified against DOF — ${source.error || 'probe did not run'}`}
      </div>
    </div>
  );
}
